import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';

import {
  approveFinding,
  getRunFindingsFacets,
  getRunFindingsSummary,
  needsReviewFinding,
  rejectFinding,
} from '@/api/findings';
import type {
  ApprovalReason,
  FindingOut,
  FindingOutcome,
  ReasonCode,
  RuleOut,
} from '@/api/types';
import type { InvoiceExtractionOut, InvoiceLinkOut } from '@/api/invoices';
import { useAuth } from '@/auth/AuthContext';
import { isAutoCleared } from '@/lib/autoCleared';
import { buildFacetOptions } from '@/lib/findingFacetOptions';
import {
  findingFilterStorageKey,
  readStoredFilters,
  writeStoredFilters,
} from '@/lib/findingFilterStorage';
import { deriveServerQueueQuery } from '@/lib/findingsPager';
import { bulkApprovalReason, normalizeApprovalReasonFilter } from '@/lib/approval-reasons';
import { findingMatchesSearch } from '@/lib/findingSearch';
import { useLockedFindingOrder } from '@/hooks/useLockedFindingOrder';
import { useServerFindingQueue } from '@/hooks/useServerFindingQueue';
import {
  invoiceFilterAccepts,
  type InvoiceFilter,
} from '@/hooks/useTransactionDocuments';

export type StatusFilter =
  | ''
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'needs_review';

/** The four review-status groups shown in the summary funnel:
 *  approved (published recovery), awaiting (flagged needs_review —
 *  still needs the practitioner's decision), unreviewed (pending),
 *  and rejected. */
export type StatusGroup = 'approved' | 'awaiting' | 'unreviewed' | 'rejected';

export const STATUS_GROUP_LABELS: Record<StatusGroup, string> = {
  approved: 'Approved',
  awaiting: 'Awaiting',
  unreviewed: 'Unreviewed',
  rejected: 'Rejected',
};

export function statusGroupFor(status: string): StatusGroup {
  if (status === 'rejected') return 'rejected';
  if (status === 'pending') return 'unreviewed';
  if (status === 'approved') return 'approved';
  // needs_review — flagged for follow-up but not yet dispositioned.
  return 'awaiting';
}

/** '' = any basis; otherwise narrows to approved findings with that basis. */
export type ApprovalReasonFilter = '' | ApprovalReason;

/** '' = both families; otherwise narrows to recovery-opportunity or
 *  compliance-risk findings. The vocabulary is closed (two outcomes), so
 *  the dropdown options are static rather than derived from the run. */
export type OutcomeFilter = '' | FindingOutcome;

export type BulkAction = 'approve' | 'reject' | 'needs_review';

// Stable no-op for the client-filtered queue mode (nothing to prefetch).
const noopEnsure = () => {};

export interface BulkProgress {
  action: BulkAction;
  done: number;
  total: number;
  errors: number;
}

interface UseFindingsReviewInput {
  findings: FindingOut[];
  rules: RuleOut[];
  runId: string;
  linksByTransaction: Map<string, InvoiceLinkOut[]>;
  extractionsByDocument: Map<string, InvoiceExtractionOut>;
  markedTransactions: Set<string>;
  linksReady: boolean;
  linksError: boolean;
}

export interface FindingFilterState {
  ruleFilter: string;
  provinceFilter: string;
  supplierFilter: string;
  transactionTypeFilter: string;
  classFilter: string;
  glAccountFilter: string;
  currencyFilter: string;
  outcomeFilter: OutcomeFilter;
  statusFilter: StatusFilter;
  approvalReasonFilter: ApprovalReasonFilter;
  reasonCodesFilter: ReasonCode[];
  amountMin: string;
  amountMax: string;
  /** Effective-rate (tax/bill, as a percentage) range. Empty = no bound. */
  effRateMin: string;
  effRateMax: string;
  carriedOnlyFilter: boolean;
  invoiceFilter: InvoiceFilter;
  /** Expense-source filter (dext | brex | ramp | direct). '' = any. */
  sourceFilter: string;
  /** Ledger-source filter (xero | qbo | netsuite). '' = any. */
  ledgerSourceFilter: string;
  /** The (already-deferred) free-text search value. Trimmed + lowercased inside. */
  search: string;
}

// Filters compose as an intersection: a finding passes when every active
// filter accepts it. Empty filter strings ignore that dimension. Pure +
// exported so the flashcard queue, the list view, and unit tests all run
// the exact same predicate — this is the parity guarantee.
export function filterFindings(
  findings: FindingOut[],
  state: FindingFilterState,
  linksByTransaction: Map<string, InvoiceLinkOut[]>,
  linksError: boolean,
  markedTransactions: Set<string> = new Set(),
  extractionsByDocument: Map<string, InvoiceExtractionOut> = new Map(),
): FindingOut[] {
  const q = state.search.trim().toLowerCase();
  const gl = state.glAccountFilter.trim().toLowerCase();
  const minNum = Number(state.amountMin);
  const maxNum = Number(state.amountMax);
  const hasMin = state.amountMin !== '' && Number.isFinite(minNum);
  const hasMax = state.amountMax !== '' && Number.isFinite(maxNum);
  const effMinNum = Number(state.effRateMin);
  const effMaxNum = Number(state.effRateMax);
  const hasEffMin = state.effRateMin !== '' && Number.isFinite(effMinNum);
  const hasEffMax = state.effRateMax !== '' && Number.isFinite(effMaxNum);
  return findings.filter((f) => {
    if (state.ruleFilter && f.rule_id !== state.ruleFilter) return false;
    if (state.provinceFilter && f.evidence['supplier.province'] !== state.provinceFilter)
      return false;
    if (state.supplierFilter && f.evidence['supplier.name'] !== state.supplierFilter)
      return false;
    if (
      state.transactionTypeFilter &&
      f.evidence['transaction_type'] !== state.transactionTypeFilter
    )
      return false;
    // Exact match on the server-derived NetSuite class. Findings with no
    // real class (null — blank and "-No Class-" both normalize to null
    // server-side) drop out while the filter is active.
    if (state.classFilter && f.class_name !== state.classFilter) return false;
    if (gl) {
      const v = f.evidence['gl_account.name'];
      if (typeof v !== 'string' || !v.toLowerCase().includes(gl)) return false;
    }
    if (state.currencyFilter && f.recovery.currency !== state.currencyFilter) return false;
    if (state.sourceFilter && f.expense_source !== state.sourceFilter) return false;
    if (state.ledgerSourceFilter && f.ledger_source !== state.ledgerSourceFilter) return false;
    if (state.outcomeFilter && f.outcome !== state.outcomeFilter) return false;
    if (state.statusFilter && f.status !== state.statusFilter) return false;
    if (state.approvalReasonFilter && f.approval_reason !== state.approvalReasonFilter)
      return false;
    if (state.carriedOnlyFilter && !f.auto_carried_forward) return false;
    // When the link lookup errored, an empty map would make every finding
    // read as "unattached" — skip the filter instead of silently
    // narrowing on wrong data (the bar shows a warning).
    if (
      !linksError &&
      !invoiceFilterAccepts(
        state.invoiceFilter,
        linksByTransaction.get(f.transaction_id),
        markedTransactions.has(f.transaction_id),
        extractionsByDocument,
      )
    )
      return false;
    if (state.reasonCodesFilter.length > 0) {
      // Any-of match: keep the finding if it carries AT LEAST one of the
      // selected codes. An empty finding.reason_codes never matches.
      const codes = f.reason_codes ?? [];
      if (!state.reasonCodesFilter.some((c) => codes.includes(c))) return false;
    }
    if (q && !findingMatchesSearch(f, q)) return false;
    if (hasMin || hasMax) {
      const amt = Number(f.recovery.amount);
      if (!Number.isFinite(amt)) return false;
      if (hasMin && amt < minNum) return false;
      if (hasMax && amt > maxNum) return false;
    }
    if (hasEffMin || hasEffMax) {
      // Effective tax rate as a percentage (tax / bill × 100). Findings
      // without a usable bill can't be scoped by rate, so they drop out
      // while the filter is active.
      const bill = Number(f.evidence['bill_amount']);
      const tax = Number(f.evidence['tax_amount']);
      // Exclude non-positive bills and negative tax (credit-note reversals),
      // which would otherwise yield a negative rate that slips past a
      // max-only bound.
      if (!Number.isFinite(bill) || bill <= 0 || !Number.isFinite(tax) || tax < 0)
        return false;
      const effPct = (100 * tax) / bill;
      if (hasEffMin && effPct < effMinNum) return false;
      if (hasEffMax && effPct > effMaxNum) return false;
    }
    return true;
  });
}

// ── Shared findings-review state ─────────────────────────────────────
//
// Owns everything the flashcard queue and the list view must share so
// they can never drift out of parity: the 13 filters, the derived
// option lists + summaries, the filtered/ordered set, and the mutation
// + bulk handlers. Presentation-only concerns (the flashcard cursor,
// keyboard nav, current-card rule lookup) stay in their view component.
export function useFindingsReview({
  findings,
  rules,
  runId,
  linksByTransaction,
  extractionsByDocument,
  markedTransactions,
  linksReady,
  linksError,
}: UseFindingsReviewInput) {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const qc = useQueryClient();

  // Two indexes into the rule catalog: one keyed by full (id@vN) for the
  // detail panel, one keyed by just id for the filter dropdown and the
  // rule-code chip's hover summary.
  const ruleIndex = useMemo(
    () => new Map<string, RuleOut>(rules.map((r) => [`${r.id}@v${r.version}`, r])),
    [rules],
  );
  const ruleById = useMemo(
    () => new Map<string, RuleOut>(rules.map((r) => [r.id, r])),
    [rules],
  );

  // Restore whatever filters were active before the practitioner navigated
  // away (e.g. into a finding detail page, which unmounts this hook) — read
  // once; the write-back effect below keeps the store current.
  const storageKey = findingFilterStorageKey(runId);
  const stored = useMemo(() => readStoredFilters(storageKey), [storageKey]);

  const [ruleFilter, setRuleFilter] = useState<string>(stored.ruleFilter ?? '');
  const [provinceFilter, setProvinceFilter] = useState<string>(stored.provinceFilter ?? '');
  const [supplierFilter, setSupplierFilter] = useState<string>(stored.supplierFilter ?? '');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<string>(
    stored.transactionTypeFilter ?? '',
  );
  // NetSuite Class — project / M&A identifiers when populated (INP-407).
  // Exact-match single-select, like Type. '' = no filter.
  const [classFilter, setClassFilter] = useState<string>(stored.classFilter ?? '');
  // Case-insensitive substring match against evidence['gl_account.name'].
  // Andrew's workflow: scope a bulk action to one GL (e.g. "6019" for
  // Norton Rose severance, "2505" for TSX Trust warrant liability) without
  // losing other active filters. Empty string = no filter.
  const [glAccountFilter, setGlAccountFilter] = useState<string>(stored.glAccountFilter ?? '');
  const [currencyFilter, setCurrencyFilter] = useState<string>(stored.currencyFilter ?? '');
  const [outcomeFilter, setOutcomeFilter] = useState<OutcomeFilter>(
    (stored.outcomeFilter as OutcomeFilter) ?? '',
  );
  // Free-text search across rule_id, transaction_id, every string in
  // evidence, the four review notes, reviewer_alias, and memo_text
  // (joined journal-entry memos). Empty string = no filter. Deferred
  // via useDeferredValue so each keystroke doesn't recompute the
  // filtered queue synchronously — the input stays responsive while
  // React schedules the heavy filter pass at lower priority.
  const [searchQuery, setSearchQuery] = useState<string>(stored.searchQuery ?? '');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  // Default to no status filter (show everything); the practitioner filters
  // to `pending` when they want the work queue. "" is the no-filter sentinel.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    (stored.statusFilter as StatusFilter) ?? '',
  );
  // Approval-basis filter — single-select; narrows to approved findings
  // marked with that basis. '' = any. Normalize the persisted value so a
  // stale sessionStorage entry can't select an option that hides every row.
  const [approvalReasonFilter, setApprovalReasonFilter] = useState<ApprovalReasonFilter>(() =>
    normalizeApprovalReasonFilter(stored.approvalReasonFilter),
  );
  // Reason-codes filter — multi-select, any-of match. When set, narrows
  // to findings whose `reason_codes` contains AT LEAST ONE of the
  // selected codes. Empty array = no filter. INP-343 (A.2).
  const [reasonCodesFilter, setReasonCodesFilter] = useState<ReasonCode[]>(
    (stored.reasonCodesFilter as ReasonCode[]) ?? [],
  );
  // Recovery amount range filter. Both are strings so the input value
  // round-trips cleanly; an empty string means "no bound on this side".
  // Parsed to Number at filter time — NaN propagates to "no bound".
  const [amountMin, setAmountMin] = useState<string>(stored.amountMin ?? '');
  const [amountMax, setAmountMax] = useState<string>(stored.amountMax ?? '');
  // Effective-rate (tax/bill %) range filter — scope to "all findings at
  // ~5%" to triage a federal-only-on-ON cluster in one pass (Ibrahim,
  // INP-372). Same string-round-trip convention as the amount range.
  const [effRateMin, setEffRateMin] = useState<string>(stored.effRateMin ?? '');
  const [effRateMax, setEffRateMax] = useState<string>(stored.effRateMax ?? '');
  // Filter chip — when true, only auto-carried-forward findings show.
  // Andrew's ask on INP-385: surface the inherited set so it can be
  // bulk-overridden or bulk-confirmed without scrolling the queue.
  const [carriedOnlyFilter, setCarriedOnlyFilter] = useState(stored.carriedOnlyFilter ?? false);
  // Three-state has-invoice filter — lets the verification pass target
  // findings whose transaction already carries an attached invoice
  // (or hunt the gaps). '' = any.
  const [invoiceFilter, setInvoiceFilter] = useState<InvoiceFilter>(
    (stored.invoiceFilter as InvoiceFilter) ?? '',
  );
  const [sourceFilter, setSourceFilter] = useState<string>(stored.sourceFilter ?? '');
  const [ledgerSourceFilter, setLedgerSourceFilter] = useState<string>(
    stored.ledgerSourceFilter ?? '',
  );

  // Monotonic "restart the queue" signal, bumped after a bulk action
  // settles. Folded into `filterKey` so a bulk reject/approve/flag
  // re-derives the locked order (and resets the flashcard cursor to the
  // top) exactly like a filter change — without it, the just-bulk-
  // dispositioned cards stay frozen in the pending queue because a
  // single-disposition keeps its slot on purpose (INP-328 / INP-373).
  const [queueEpoch, setQueueEpoch] = useState(0);
  const restartQueue = () => setQueueEpoch((n) => n + 1);

  // The List view reports whether a column sort is applied. A sort spans
  // the whole set (client-side reorder), so it forces the full-set load
  // + disables the server-paged path. The List owns the sort state; it
  // pushes the boolean up via `setListSortActive`.
  const [listSortActive, setListSortActive] = useState(false);

  // Persist the active filters per run so they survive navigating into a
  // finding detail page (and a reload) — the shared state unmounts with
  // FindingsTabs otherwise.
  useEffect(() => {
    writeStoredFilters(storageKey, {
      ruleFilter,
      provinceFilter,
      supplierFilter,
      transactionTypeFilter,
      classFilter,
      glAccountFilter,
      currencyFilter,
      outcomeFilter,
      searchQuery,
      statusFilter,
      approvalReasonFilter,
      reasonCodesFilter,
      amountMin,
      amountMax,
      effRateMin,
      effRateMax,
      carriedOnlyFilter,
      invoiceFilter,
      sourceFilter,
      ledgerSourceFilter,
    });
  }, [
    storageKey,
    ruleFilter,
    provinceFilter,
    supplierFilter,
    transactionTypeFilter,
    classFilter,
    glAccountFilter,
    currencyFilter,
    outcomeFilter,
    searchQuery,
    statusFilter,
    approvalReasonFilter,
    reasonCodesFilter,
    amountMin,
    amountMax,
    effRateMin,
    effRateMax,
    carriedOnlyFilter,
    invoiceFilter,
    sourceFilter,
    ledgerSourceFilter,
  ]);

  // Filter-dropdown options come from the server facets endpoint (one
  // grouped query per facet) so the option lists are complete even when
  // only a slice of findings is loaded (server-paged List/Queue). Falls
  // back to enumerating the loaded findings when the facets haven't
  // landed. `class` + `pendingBySupplier` stay client-derived below
  // (degraded — reflect only loaded rows under paging) since the facets
  // endpoint doesn't ship them.
  const facetsQuery = useQuery({
    queryKey: ['findings', 'facets', runId, userKey],
    enabled: isSignedIn,
    queryFn: async () => getRunFindingsFacets(runId, (await getIdToken()) ?? ''),
  });
  const facetOptions = useMemo(
    () => buildFacetOptions(facetsQuery.data, findings),
    [facetsQuery.data, findings],
  );

  const ruleIds = facetOptions.ruleIds;
  const provinces = facetOptions.provinces;
  const suppliers = facetOptions.suppliers;

  // Pending findings grouped by supplier — drives the per-supplier
  // bulk-action buttons on each FindingCard. Counts only `pending` to
  // match `startBulkForSupplier`, which scopes the bulk dialog to
  // `statusFilter='pending'`. needs_review findings are tracked by the
  // practitioner separately and aren't pulled into supplier-level bulks
  // — bulk-flipping a flagged item back to pending or approved should
  // be an intentional per-finding action.
  const pendingBySupplier = useMemo(() => {
    const counts = new Map<string, number>();
    for (const f of findings) {
      if (f.status !== 'pending') continue;
      const s = f.evidence['supplier.name'];
      if (typeof s !== 'string' || s.length === 0) continue;
      counts.set(s, (counts.get(s) ?? 0) + 1);
    }
    return counts;
  }, [findings]);

  const transactionTypes = facetOptions.transactionTypes;

  // Classes that actually fire on this run — null (no class) never
  // appears as an option, so the dropdown is omitted entirely on runs
  // whose source has no Class column (QBO, Xero).
  const classes = useMemo(() => {
    const set = new Set<string>();
    for (const f of findings) {
      if (typeof f.class_name === 'string' && f.class_name.length > 0) set.add(f.class_name);
    }
    return Array.from(set).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [findings]);

  // Currency / expense-source / ledger-source options — from the server
  // facets when loaded (complete under paging), else the loaded findings.
  // Expense + ledger sources are intersected with their fixed review-order
  // whitelists inside `buildFacetOptions`.
  const currencies = facetOptions.currencies;
  const sources = facetOptions.sources;
  const ledgerSources = facetOptions.ledgerSources;

  const filtered = useMemo(
    () =>
      filterFindings(
        findings,
        {
          ruleFilter,
          provinceFilter,
          supplierFilter,
          transactionTypeFilter,
          classFilter,
          glAccountFilter,
          currencyFilter,
          outcomeFilter,
          statusFilter,
          approvalReasonFilter,
          reasonCodesFilter,
          amountMin,
          amountMax,
          effRateMin,
          effRateMax,
          carriedOnlyFilter,
          invoiceFilter,
          sourceFilter,
          ledgerSourceFilter,
          search: deferredSearchQuery,
        },
        linksByTransaction,
        linksError,
        markedTransactions,
        extractionsByDocument,
      ),
    [
      findings,
      markedTransactions,
      extractionsByDocument,
      ruleFilter,
      provinceFilter,
      supplierFilter,
      transactionTypeFilter,
      classFilter,
      glAccountFilter,
      currencyFilter,
      outcomeFilter,
      statusFilter,
      approvalReasonFilter,
      carriedOnlyFilter,
      invoiceFilter,
      sourceFilter,
      ledgerSourceFilter,
      linksByTransaction,
      linksError,
      reasonCodesFilter,
      deferredSearchQuery,
      amountMin,
      amountMax,
      effRateMin,
      effRateMax,
    ],
  );

  // One key over every filter input — a change means the practitioner
  // committed a new queue, so the locked order re-derives and the cursor
  // restarts at the top.
  const filterKey = JSON.stringify([
    ruleFilter,
    provinceFilter,
    supplierFilter,
    transactionTypeFilter,
    classFilter,
    glAccountFilter,
    currencyFilter,
    outcomeFilter,
    statusFilter,
    approvalReasonFilter,
    carriedOnlyFilter,
    invoiceFilter,
    sourceFilter,
    ledgerSourceFilter,
    // The invoice filter's membership set changes when the batched
    // link lookup lands (or errors, which deactivates it) — fold that
    // into the key ONLY while the filter is active so the locked order
    // re-derives then, without resetting everyone else's cursor.
    invoiceFilter === '' ? true : linksReady && !linksError,
    reasonCodesFilter,
    deferredSearchQuery,
    amountMin,
    amountMax,
    effRateMin,
    effRateMax,
    // A bulk action bumps this once its refetch settles, forcing the
    // locked order + cursor to restart against the post-bulk statuses.
    queueEpoch,
  ]);

  const orderedFindings = useLockedFindingOrder(findings, filtered, filterKey);

  // ── Server-cursor queue + server aggregates (INP-778) ──────────────

  const findingById = useMemo(
    () => new Map(findings.map((f) => [f.finding_id, f])),
    [findings],
  );

  // When the caller's list includes auto-cleared coverage rows the
  // "show auto-cleared" toggle is on — a client-side view the server
  // pages don't model, so both the paged queue and the server summary
  // fall back to the client-computed path.
  const includesAutoCleared = useMemo(() => findings.some(isAutoCleared), [findings]);

  // Pushdown-able filter combos are served by server pages; anything
  // touching a client-only filter returns null and the queue falls back
  // to the classic full-set client filter.
  const serverQueueQuery = useMemo(
    () =>
      deriveServerQueueQuery(
        {
          statusFilter,
          outcomeFilter,
          sourceFilter,
          ledgerSourceFilter,
          invoiceFilter,
          ruleFilter,
          provinceFilter,
          supplierFilter,
          transactionTypeFilter,
          classFilter,
          glAccountFilter,
          currencyFilter,
          approvalReasonFilter,
          reasonCodesCount: reasonCodesFilter.length,
          amountMin,
          amountMax,
          effRateMin,
          effRateMax,
          carriedOnlyFilter,
          search: deferredSearchQuery,
          listSortActive,
        },
        includesAutoCleared,
      ),
    [
      statusFilter,
      outcomeFilter,
      sourceFilter,
      ledgerSourceFilter,
      invoiceFilter,
      ruleFilter,
      provinceFilter,
      supplierFilter,
      transactionTypeFilter,
      classFilter,
      glAccountFilter,
      currencyFilter,
      approvalReasonFilter,
      reasonCodesFilter,
      amountMin,
      amountMax,
      effRateMin,
      effRateMax,
      carriedOnlyFilter,
      deferredSearchQuery,
      includesAutoCleared,
      listSortActive,
    ],
  );

  const serverQueue = useServerFindingQueue({
    runId,
    enabled: serverQueueQuery !== null,
    query: serverQueueQuery,
    queueKey: filterKey,
    liveById: findingById,
  });

  // The flashcard queue's card source: server pages when the filter
  // combo pushes down (and the first page has landed), else the locked
  // client order. Both share the same review ordering, so the one-time
  // switchover after the first page lands is visually a no-op.
  const serverPaged = serverQueueQuery !== null && serverQueue.ready;
  // Bulk-on-filtered acts on `filtered`, which in server-paged mode only
  // covers the pages loaded so far — running it would silently under-act.
  // So it's only available on the full-set client path. (The List's own
  // bulk-on-SELECTED is page-scoped and stays correct regardless.) The
  // per-supplier bulk flow forces the full-set path via supplierFilter
  // (a client-only filter → serverQueueQuery null → !serverPaged) before
  // opening its dialog, so it's unaffected.
  const bulkOnFilteredAvailable = !serverPaged;
  const queueItems = serverPaged ? serverQueue.items : orderedFindings;
  const queueTotal = serverPaged
    ? serverQueue.total ?? serverQueue.items.length
    : orderedFindings.length;
  const ensureQueueIndex = serverPaged ? serverQueue.ensureLoadedThrough : noopEnsure;
  // Surfaced whenever the paged path is eligible (even pre-ready — a
  // failed FIRST page leaves the queue on the client fallback, and the
  // practitioner should still see why + be able to retry).
  const queueError = serverQueueQuery !== null && serverQueue.error;
  const retryQueue = serverQueue.retry;

  // Run-level aggregates from the server (one GROUP BY) so the chips /
  // progress bar never depend on how many rows are loaded client-side.
  // Keyed under the by-run prefix so every existing disposition
  // invalidation refetches it too.
  const summaryQuery = useQuery({
    queryKey: ['findings', 'by-run', runId, userKey, 'summary'],
    enabled: isSignedIn,
    queryFn: async () => getRunFindingsSummary(runId, (await getIdToken()) ?? ''),
  });
  const serverSummary = includesAutoCleared ? undefined : summaryQuery.data;

  // Invoice-attachment progress over the whole run: how many findings
  // have paper attached. Server-aggregated when available (the loaded
  // link map only covers loaded pages); client fallback otherwise.
  const clientAttachedCount = useMemo(() => {
    let attached = 0;
    for (const f of findings) {
      const links = linksByTransaction.get(f.transaction_id);
      if (!links || links.length === 0) continue;
      attached += 1;
    }
    return attached;
  }, [findings, linksByTransaction]);
  const attachedCount = serverSummary ? serverSummary.attached : clientAttachedCount;

  // Summary aggregates over the FULL list (ignoring the rule filter) so
  // the practitioner always sees overall progress.
  const summary = useMemo(() => {
    const stats = {
      approved: { count: 0, total: 0 },
      rejected: { count: 0, total: 0 },
      needs_review: { count: 0, total: 0 },
      pending: { count: 0, total: 0 },
    };
    for (const f of findings) {
      const entry = stats[f.status as keyof typeof stats];
      if (entry) {
        entry.count += 1;
        // Single-currency rollup. Honest only when every finding in the
        // run shares one currency (today, NVG + Powell are CAD-only).
        // Mixed-currency runs need a `CurrencySum[]` shape so the
        // formatter can render "+ N other" instead of mis-totalling.
        entry.total += Number(f.recovery.amount) || 0;
      }
    }
    return stats;
  }, [findings]);

  // The status funnel: approved, awaiting (flagged needs_review),
  // unreviewed (pending), and rejected. `count` is every finding in the
  // group; `total` sums recovery-outcome dollars only — compliance-risk
  // exposure is reported separately in the header, so folding it into
  // these chips would conflate the two. Sourced from the server's
  // GROUP BY when available (honest under pagination); the client pass
  // over the loaded list is the fallback (and the auto-cleared-visible
  // view, which the server summary deliberately excludes).
  const clientStatusSummary = useMemo(() => {
    const stats: Record<StatusGroup, { count: number; total: number }> = {
      approved: { count: 0, total: 0 },
      awaiting: { count: 0, total: 0 },
      unreviewed: { count: 0, total: 0 },
      rejected: { count: 0, total: 0 },
    };
    for (const f of findings) {
      const group = statusGroupFor(f.status);
      const g = stats[group];
      g.count += 1;
      if (f.outcome === 'recovery') g.total += Number(f.recovery.amount) || 0;
    }
    return stats;
  }, [findings]);
  const statusSummary = useMemo(() => {
    if (!serverSummary) return clientStatusSummary;
    const bucket = (b: { count: number; recovery_total: string }) => ({
      count: b.count,
      total: Number(b.recovery_total) || 0,
    });
    return {
      approved: bucket(serverSummary.approved),
      awaiting: bucket(serverSummary.needs_review),
      unreviewed: bucket(serverSummary.pending),
      rejected: bucket(serverSummary.rejected),
    };
  }, [serverSummary, clientStatusSummary]);

  // `reviewedCount` is the practitioner's final-decision count; needs_review
  // is un-dispositioned (a "come back to this" flag) so it doesn't count
  // toward reviewed — same treatment as pending. Mirrors the backend
  // review_counts_for_runs `reviewed` set. Server aggregates win when
  // available so the progress bar is honest under pagination.
  const reviewedCount = serverSummary
    ? serverSummary.approved.count + serverSummary.rejected.count
    : summary.approved.count + summary.rejected.count;
  const totalCount = serverSummary ? serverSummary.total : findings.length;
  const reviewedPct =
    totalCount === 0 ? 0 : Math.round((reviewedCount / totalCount) * 100);

  const hasActiveFilter = Boolean(
    ruleFilter ||
      provinceFilter ||
      supplierFilter ||
      transactionTypeFilter ||
      classFilter ||
      glAccountFilter.trim() ||
      currencyFilter ||
      statusFilter ||
      approvalReasonFilter ||
      carriedOnlyFilter ||
      invoiceFilter ||
      sourceFilter ||
      ledgerSourceFilter ||
      searchQuery.trim() ||
      amountMin ||
      amountMax ||
      effRateMin ||
      effRateMax ||
      reasonCodesFilter.length > 0,
  );
  // Bulk actions target the entire filtered set, regardless of current
  // status — re-review is allowed, so bulk-approving a filter of
  // already-rejected findings flips them all to approved.
  // Single-currency total. Today's engagements are mono-currency CAD,
  // so summing raw `recovery.amount` across the filtered findings is
  // honest. Mixed-currency runs need a `CurrencySum[]` shape that
  // groups by `recovery.currency` before summing.
  const filteredTotal = useMemo(
    () => filtered.reduce((acc, f) => acc + (Number(f.recovery.amount) || 0), 0),
    [filtered],
  );

  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);
  // Required note recorded against every finding in the bulk-approve
  // set — full parity with bulk-Reject + bulk-Flag (same min-height,
  // same required, same disabled-when-empty submit). The practitioner
  // who reviews the filing later needs to know *why* a cluster was
  // approved; making it required avoids the silent-approval pattern
  // Andrew flagged on the NVG engagement.
  const [bulkApproveNote, setBulkApproveNote] = useState('');
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  // Reason codes for the bulk-reject form — same set applied to every
  // finding in the batch. Reset alongside the free-text reason.
  const [bulkRejectCodes, setBulkRejectCodes] = useState<ReasonCode[]>([]);
  const [bulkNeedsReviewOpen, setBulkNeedsReviewOpen] = useState(false);
  const [bulkNeedsReviewNote, setBulkNeedsReviewNote] = useState('');
  const [bulkProgress, setBulkProgress] = useState<BulkProgress | null>(null);

  const clearAllFilters = () => {
    setRuleFilter('');
    setProvinceFilter('');
    setTransactionTypeFilter('');
    setClassFilter('');
    setGlAccountFilter('');
    setCurrencyFilter('');
    setOutcomeFilter('');
    setSupplierFilter('');
    setStatusFilter('');
    setApprovalReasonFilter('');
    setSearchQuery('');
    setAmountMin('');
    setAmountMax('');
    setEffRateMin('');
    setEffRateMax('');
    setReasonCodesFilter([]);
    setApprovalReasonFilter('');
    setCarriedOnlyFilter(false);
    setInvoiceFilter('');
    setSourceFilter('');
    setLedgerSourceFilter('');
  };

  // Drop scoping filters but keep the status filter. Called after a bulk
  // action completes so the practitioner doesn't sit looking at an empty
  // page when the scope they just dispositioned drains out — instead the
  // next batch in their queue (still scoped by status, typically `pending`)
  // surfaces immediately.
  const clearScopeFilters = () => {
    setRuleFilter('');
    setProvinceFilter('');
    setTransactionTypeFilter('');
    setClassFilter('');
    setGlAccountFilter('');
    setCurrencyFilter('');
    setOutcomeFilter('');
    setSupplierFilter('');
    setSearchQuery('');
    setAmountMin('');
    setAmountMax('');
    setEffRateMin('');
    setEffRateMax('');
    setReasonCodesFilter([]);
    setApprovalReasonFilter('');
    setCarriedOnlyFilter(false);
    setInvoiceFilter('');
    setSourceFilter('');
    setLedgerSourceFilter('');
  };

  const runBulkApprove = async () => {
    if (!isSignedIn) return;
    // `filtered` only spans loaded pages under server paging — refuse to
    // bulk-act on a partial set rather than silently under-act.
    if (!bulkOnFilteredAvailable) {
      console.warn('bulk approve skipped: filtered set is server-paged (partial)');
      return;
    }
    const note = bulkApproveNote.trim();
    if (note.length === 0) return;
    const targets = filtered;
    if (targets.length === 0) return;
    // Resolve the token once — a null token would otherwise send every
    // mutation with an empty bearer and fail the whole batch silently.
    const idToken = await getIdToken();
    if (!idToken) return;
    setBulkProgress({ action: 'approve', done: 0, total: targets.length, errors: 0 });
    let errors = 0;
    for (let i = 0; i < targets.length; i++) {
      try {
        await approveFinding(
          targets[i].finding_id,
          idToken,
          bulkApprovalReason(targets[i].approval_reason),
          note,
        );
      } catch (e) {
        errors += 1;
        console.error('bulk approve failed for', targets[i].finding_id, e);
      }
      setBulkProgress({
        action: 'approve',
        done: i + 1,
        total: targets.length,
        errors,
      });
    }
    setBulkApproveOpen(false);
    setBulkApproveNote('');
    qc.invalidateQueries({ queryKey: ['runs', runId, userKey] });
    // Await the findings refetch before restarting the queue so the
    // re-lock captures the post-bulk statuses. Restarting against the
    // pre-approve snapshot would freeze the just-dispositioned cards in
    // the pending queue instead of draining them.
    await qc.invalidateQueries({ queryKey: ['findings', 'by-run', runId, userKey] });
    clearScopeFilters();
    restartQueue();
    window.setTimeout(() => setBulkProgress(null), 1500);
  };

  const runBulkNeedsReview = async () => {
    if (!isSignedIn) return;
    if (!bulkOnFilteredAvailable) {
      console.warn('bulk needs-review skipped: filtered set is server-paged (partial)');
      return;
    }
    const reviewNote = bulkNeedsReviewNote.trim();
    if (reviewNote.length === 0) return;
    const targets = filtered;
    if (targets.length === 0) return;
    const idToken = await getIdToken();
    if (!idToken) return;
    setBulkProgress({ action: 'needs_review', done: 0, total: targets.length, errors: 0 });
    let errors = 0;
    for (let i = 0; i < targets.length; i++) {
      try {
        await needsReviewFinding(targets[i].finding_id, reviewNote, idToken);
      } catch (e) {
        errors += 1;
        console.error('bulk needs-review failed for', targets[i].finding_id, e);
      }
      setBulkProgress({
        action: 'needs_review',
        done: i + 1,
        total: targets.length,
        errors,
      });
    }
    setBulkNeedsReviewOpen(false);
    setBulkNeedsReviewNote('');
    qc.invalidateQueries({ queryKey: ['runs', runId, userKey] });
    // Await the findings refetch before restarting the queue so the
    // re-lock captures the post-bulk statuses (see runBulkApprove).
    await qc.invalidateQueries({ queryKey: ['findings', 'by-run', runId, userKey] });
    clearScopeFilters();
    restartQueue();
    window.setTimeout(() => setBulkProgress(null), 1500);
  };

  const runBulkReject = async () => {
    if (!isSignedIn) return;
    if (!bulkOnFilteredAvailable) {
      console.warn('bulk reject skipped: filtered set is server-paged (partial)');
      return;
    }
    const reason = bulkRejectReason.trim();
    // Snapshot the codes once so a mid-loop edit can't poison the batch.
    const codes = bulkRejectCodes;
    // At least one signal required — codes OR text.
    if (reason.length === 0 && codes.length === 0) return;
    const targets = filtered;
    if (targets.length === 0) return;
    const idToken = await getIdToken();
    if (!idToken) return;
    setBulkProgress({ action: 'reject', done: 0, total: targets.length, errors: 0 });
    let errors = 0;
    for (let i = 0; i < targets.length; i++) {
      try {
        await rejectFinding(targets[i].finding_id, reason, idToken, codes);
      } catch (e) {
        errors += 1;
        console.error('bulk reject failed for', targets[i].finding_id, e);
      }
      setBulkProgress({
        action: 'reject',
        done: i + 1,
        total: targets.length,
        errors,
      });
    }
    setBulkRejectOpen(false);
    setBulkRejectReason('');
    setBulkRejectCodes([]);
    qc.invalidateQueries({ queryKey: ['runs', runId, userKey] });
    // Await the findings refetch before restarting the queue so the
    // re-lock captures the post-bulk statuses (see runBulkApprove). This
    // is the Ibrahim bug: bulk-rejecting a supplier under the pending
    // filter left the just-rejected cards frozen at the top of the queue
    // instead of advancing to the next pending finding.
    await qc.invalidateQueries({ queryKey: ['findings', 'by-run', runId, userKey] });
    clearScopeFilters();
    restartQueue();
    window.setTimeout(() => setBulkProgress(null), 1500);
  };

  // "Apply to all from {supplier}" handlers: scope the filter set to
  // `(supplier=X, status=pending)` so the existing bulk-action dialog
  // operates on exactly those rows, then open the dialog. The dialog
  // reads `filtered` to count the targets and display the blast radius,
  // so no separate target list is needed — filter state IS the target.
  const startBulkForSupplier = (supplier: string, action: BulkAction) => {
    setRuleFilter('');
    setProvinceFilter('');
    setTransactionTypeFilter('');
    setClassFilter('');
    setGlAccountFilter('');
    setCurrencyFilter('');
    setSupplierFilter(supplier);
    setStatusFilter('pending');
    setSearchQuery('');
    setAmountMin('');
    setAmountMax('');
    setEffRateMin('');
    setEffRateMax('');
    // Clear every other scoping filter too — a stale reason-code /
    // carried-only / invoice narrow would silently truncate the "all
    // from supplier X" target set (or zero it out).
    setReasonCodesFilter([]);
    setCarriedOnlyFilter(false);
    setInvoiceFilter('');
    setSourceFilter('');
    setLedgerSourceFilter('');
    if (action === 'approve') {
      setBulkApproveOpen(true);
    } else if (action === 'reject') {
      setBulkRejectOpen(true);
    } else {
      setBulkNeedsReviewOpen(true);
    }
  };

  return {
    // rule catalog
    ruleIndex,
    ruleById,
    // filter state
    ruleFilter,
    setRuleFilter,
    provinceFilter,
    setProvinceFilter,
    supplierFilter,
    setSupplierFilter,
    transactionTypeFilter,
    setTransactionTypeFilter,
    classFilter,
    setClassFilter,
    glAccountFilter,
    setGlAccountFilter,
    currencyFilter,
    setCurrencyFilter,
    outcomeFilter,
    setOutcomeFilter,
    searchQuery,
    setSearchQuery,
    statusFilter,
    approvalReasonFilter,
    setStatusFilter,
    setApprovalReasonFilter,
    reasonCodesFilter,
    setReasonCodesFilter,
    amountMin,
    setAmountMin,
    amountMax,
    setAmountMax,
    effRateMin,
    setEffRateMin,
    effRateMax,
    setEffRateMax,
    carriedOnlyFilter,
    setCarriedOnlyFilter,
    invoiceFilter,
    setInvoiceFilter,
    sourceFilter,
    setSourceFilter,
    ledgerSourceFilter,
    setLedgerSourceFilter,
    // derived option lists
    ruleIds,
    sources,
    ledgerSources,
    provinces,
    suppliers,
    transactionTypes,
    classes,
    currencies,
    pendingBySupplier,
    // computed sets
    filtered,
    filterKey,
    orderedFindings,
    hasActiveFilter,
    filteredTotal,
    // server-cursor queue (INP-778) — the flashcard card source. In
    // client-filtered mode these mirror orderedFindings; in paged mode
    // items grow as pages land, total is the locked server count, and
    // ensureQueueIndex prefetches ahead of the cursor.
    queueItems,
    queueTotal,
    ensureQueueIndex,
    queueError,
    retryQueue,
    serverPaged,
    // The List view reports its column-sort state up so the pushdown +
    // full-load decision can force the client path when a sort is active.
    listSortActive,
    setListSortActive,
    // Whether bulk-on-filtered is honest (full-set path). False under
    // server paging, where `filtered` covers only loaded pages.
    bulkOnFilteredAvailable,
    // True when a surface needs the whole finding set loaded client-side:
    // a client-only filter is active, the view includes auto-cleared rows,
    // or the List has a column sort. `serverQueueQuery === null` captures
    // all three (deriveServerQueueQuery bails on client-only filters,
    // includesAutoCleared, and listSortActive). The route ORs in the
    // full-set tabs (audit / suppliers / glAccounts).
    needsClientFullLoad: serverQueueQuery === null,
    // summaries
    summary,
    statusSummary,
    attachedCount,
    reviewedCount,
    totalCount,
    reviewedPct,
    // bulk state
    bulkApproveOpen,
    setBulkApproveOpen,
    bulkApproveNote,
    setBulkApproveNote,
    bulkRejectOpen,
    setBulkRejectOpen,
    bulkRejectReason,
    setBulkRejectReason,
    bulkRejectCodes,
    setBulkRejectCodes,
    bulkNeedsReviewOpen,
    setBulkNeedsReviewOpen,
    bulkNeedsReviewNote,
    setBulkNeedsReviewNote,
    bulkProgress,
    // handlers
    runBulkApprove,
    runBulkNeedsReview,
    runBulkReject,
    clearAllFilters,
    clearScopeFilters,
    startBulkForSupplier,
  };
}

export type FindingsReview = ReturnType<typeof useFindingsReview>;
